/** The surface object pass: what goes in a home zone, a junction, a treasure
 * zone, a dead end and a sea, plus the map-wide structures - portal pairs,
 * whirlpools, one-way portals, Mystery Caskets, Oracle's Huts, the gate guards
 * and the vault.
 *
 * Everything here is a table of `place` calls over `placement.ts`; the
 * budgets scale with zone area (`helpings`) so a large map is populated
 * rather than diluted.
 */

import type { Zone } from "./biomes.js";
import { SCENERY, STARTING_MINES } from "./catalog.js";
import type { Grid } from "./grid.js";
import { allNeighbours, chebyshev, idx, inside, terrainOf, walkable, xOf, yOf } from "./grid.js";
import { START_GARRISON_VALUE } from "./neutralTowns.js";
import type { Payload } from "./place.js";
import {
  HAMLET_RADIUS, around, artifactPayload, dangerBand, dropObject, garrisonPayload, guardAt,
  hamlets, near, nothing, pickWanderer, place, placeGuard, poolOf, shrinePayload, themed,
} from "./placement.js";
import { int, pick, shuffle } from "./rng.js";
import type { TownSite } from "./siting.js";
import type { GenState } from "./state.js";
import { clamp } from "./state.js";

/**
 * A starting neighbourhood: two guaranteed mines, a purse of resources, a
 * handful of visitables and only the mildest guards. The classic's opening week is
 * about building, not fighting, and a random map that puts a Wyrm Hoard on
 * the doorstep has failed before the first turn.
 */
/**
 * A zone's share of the standard helping of content, from its own area.
 *
 * Every budget below used to be a small fixed count - four resource piles, one
 * mine, two prize visitables - and the number of zones is `players * 3 + 4`
 * whatever the map measures. So the same thirty-odd things were spread over a
 * zone of a hundred and fifty cells on a small map and five thousand nine
 * hundred on a very large one, and the density of things worth walking to fell
 * off a cliff with size. Measured, interactables per thousand walkable cells:
 *
 *     48x48   151        144x144   30
 *     72x72    86        252x252   12   (and 266 of its 494 were wandering
 *                                        monsters, so ~100 things to DO on
 *                                        forty-one thousand cells of ground)
 *
 * That is the "blank slate of grassland" a big map turned into: a hero could
 * march for twenty turns across an empty continent. The budget is now per
 * ZONE AREA, so a map four times the size carries four times the content and
 * exploring a Massive map is exploring rather than commuting.
 *
 * `ZONE_HELPING` is the 72x72 zone - the size the fixed budgets were tuned
 * against - so a 72x72 map is left almost exactly where it was.
 */
const ZONE_HELPING = 320;
/** Past this a zone is a region and more of the same stops adding anything. */
const MAX_HELPINGS = 22;

function helpings(cells: number): number {
  return clamp(Math.round(cells / ZONE_HELPING), 1, MAX_HELPINGS);
}

/** The same, damped: for content that shapes the economy rather than dressing
 *  the ground, where four times the map does not want four times the mines. */
const economyHelpings = (h: number): number => Math.max(1, Math.round(1 + (h - 1) * 0.65));

export function populateHomeZones(
  s: GenState, playerZones: readonly Zone[], sites: readonly (TownSite | null)[],
): void {
  const cat = poolOf(s);
  playerZones.forEach((zone, p) => {
    if (sites[p] === null || sites[p] === undefined) return;
    const cells = zone.cells.filter((i) => walkable(s.map, i));
    const h = helpings(cells.length);
    const hEcon = economyHelpings(h);
    for (const defId of STARTING_MINES) place(s, cells, defId, nothing, 60);
    const extraMines = cat.mines.filter((x) => !STARTING_MINES.includes(x));
    const extra = themed(s, extraMines, zone);
    if (extra !== null) place(s, cells, extra, nothing, 40);

    // Two caches rather than a sprinkle. A player's opening week is a route,
    // and a route wants somewhere to go: eight piles spread evenly over the
    // home zone is eight identical errands, two heaps of four is two.
    const caches = hamlets(s, cells, 2 + Math.trunc(h / 2));
    for (let k = 0; k < (8 + int(s.r, 5)) * h; k++) {
      const defId = pick(s.r, cat.resources);
      if (defId !== null) place(s, near(s, cells, caches), defId, nothing, 12);
    }
    for (let k = 0; k < (4 + int(s.r, 3)) * h; k++) {
      const defId = pick(s.r, cat.homeBonuses);
      if (defId !== null) place(s, cells, defId, nothing, 20);
    }
    for (let k = 0; k < hEcon; k++) {
      const dwelling = themed(s, cat.dwellings, zone);
      if (dwelling !== null) place(s, cells, dwelling, nothing, 30);
    }
    for (let k = 0; k < economyHelpings(h); k++) place(s, cells, "bld_obelisk", nothing, 20);

    for (let k = 0; k < (3 + int(s.r, 3)) * h; k++) {
      const cell = cells[int(s.r, cells.length)];
      if (cell === undefined) continue;
      placeGuard(s, cell, Math.min(1, dangerBand(s, cell)));
    }
  });
}

/** Everything worth fighting for lives outside anybody's home zone. */
export function populateNeutralZones(s: GenState): void {
  for (const zone of s.zones) {
    if (zone.job === "starting") continue;
    const water = zone.job === "water" || zone.terrain === "water";
    const cells = zone.cells.filter((i) =>
      water ? terrainOf(s.map, i) === "water" : walkable(s.map, i));
    if (cells.length < 12) continue;
    const band = dangerBand(s, zone.seed);
    if (water) {
      populateWaterZone(s, zone, cells, band);
      continue;
    }
    if (zone.job === "dead-end") populateDeadEnd(s, zone, cells, band);
    else if (zone.job === "treasure") populateTreasure(s, zone, cells, band);
    else populateJunction(s, zone, cells, band);
  }
}

function scatterResources(
  s: GenState, cells: readonly number[], spots: readonly number[],
  n: number, band: number, guardFrom: number,
): void {
  const cat = poolOf(s);
  for (let k = 0; k < n; k++) {
    const defId = pick(s.r, cat.resources);
    if (defId === null) continue;
    const at = place(s, near(s, cells, spots), defId, nothing, 12);
    if (at >= 0 && band >= guardFrom) guardAt(s, at, band);
  }
}

function scatterMines(
  s: GenState, zone: Zone, cells: readonly number[], n: number, band: number,
): void {
  const cat = poolOf(s);
  for (let k = 0; k < n; k++) {
    const defId = themed(s, cat.mines, zone);
    if (defId === null) continue;
    const mine = place(s, cells, defId, nothing, 25);
    guardAt(s, mine, band);
    const door = mine < 0 ? undefined : s.placements[mine]?.entrances[0];
    if (door === undefined) continue;
    for (let j = 0; j < 1 + int(s.r, 2); j++) {
      const pile = pick(s.r, cat.resources);
      if (pile !== null) place(s, around(s.g, cells, door, 3), pile, nothing, 8);
    }
  }
}

function scatterDwellings(
  s: GenState, zone: Zone, cells: readonly number[], village: number | null, n: number,
): void {
  const cat = poolOf(s);
  for (let k = 0; k < n; k++) {
    const defId = themed(s, cat.dwellings, zone);
    if (defId === null) continue;
    place(s, village === null ? cells : around(s.g, cells, village, HAMLET_RADIUS),
      defId, nothing, 25);
  }
}

function scatterGuards(s: GenState, cells: readonly number[], n: number, band: number): void {
  for (let k = 0; k < n; k++) {
    const cell = cells[int(s.r, cells.length)];
    if (cell === undefined) continue;
    placeGuard(s, cell, band);
  }
}

/** The corridor between kingdoms: mines, dwellings, a few prizes. */
function populateJunction(
  s: GenState, zone: Zone, cells: readonly number[], band: number,
): void {
  const cat = poolOf(s);
  const h = helpings(cells.length);
  const hEcon = economyHelpings(h);
  const spots = hamlets(s, cells, (2 + int(s.r, 2)) + Math.trunc(h / 2));
  scatterResources(s, cells, spots, (4 + int(s.r, 5)) * h, band, 3);
  for (let k = 0; k < (1 + int(s.r, 2)) * h; k++) {
    const defId = pick(s.r, cat.chests);
    if (defId !== null) place(s, near(s, cells, spots), defId, nothing, 12);
  }
  scatterMines(s, zone, cells, (1 + int(s.r, 2)) * hEcon, band);
  scatterDwellings(s, zone, cells, pick(s.r, spots), hEcon);
  for (let k = 0; k < (2 + int(s.r, 3)) * h; k++) {
    const defId = pick(s.r, cat.prizeBonuses);
    if (defId !== null) place(s, cells, defId, nothing, 20);
  }
  for (let k = 0; k < economyHelpings(h); k++) place(s, cells, "bld_obelisk", nothing, 15);
  if (band >= 2) place(s, cells, "tp_garrison", () => garrisonPayload(s, band), 20);
  scatterGuards(s, cells, (2 + int(s.r, 3)) * h, band);
}

/** Interior land: banks, relics, shrines, the things a player crosses for. */
function populateTreasure(
  s: GenState, zone: Zone, cells: readonly number[], band: number,
): void {
  const cat = poolOf(s);
  const h = helpings(cells.length);
  const hEcon = economyHelpings(h);
  const spots = hamlets(s, cells, (2 + int(s.r, 2)) + Math.trunc(h / 2));
  scatterResources(s, cells, spots, (2 + int(s.r, 3)) * h, band, 2);
  for (let k = 0; k < (2 + int(s.r, 3)) * h; k++) {
    const defId = pick(s.r, cat.chests);
    if (defId !== null) place(s, near(s, cells, spots), defId, nothing, 12);
  }
  scatterMines(s, zone, cells, hEcon, band);
  scatterDwellings(s, zone, cells, pick(s.r, spots), Math.max(1, hEcon - 1));
  for (let k = 0; k < (2 + int(s.r, 3)) * h; k++) {
    const defId = pick(s.r, cat.prizeBonuses);
    if (defId !== null) place(s, cells, defId, nothing, 20);
  }
  for (let k = 0; k < hEcon; k++) {
    const shrine = pick(s.r, cat.shrines);
    if (shrine !== null) place(s, cells, shrine, () => shrinePayload(s, band), 20);
  }
  for (let k = 0; k < hEcon; k++) {
    const bank = themed(s, cat.banks, zone);
    if (bank !== null) guardAt(s, place(s, cells, bank, nothing, 25), band + 1);
  }
  for (let k = 0; k < (1 + int(s.r, 2)) * hEcon; k++) {
    const at = place(s, cells, "pu_artifact", () => artifactPayload(s, band), 15);
    if (at >= 0) guardAt(s, at, band + 1);
  }
  for (let k = 0; k < hEcon; k++) placeCaskets(s, cells, band);
  for (let k = 0; k < economyHelpings(h); k++) place(s, cells, "bld_obelisk", nothing, 15);
  scatterGuards(s, cells, (2 + int(s.r, 4)) * h, band);
}

/** A cul-de-sac: few objects, each one expensive, guards a band up. */
function populateDeadEnd(
  s: GenState, zone: Zone, cells: readonly number[], band: number,
): void {
  const cat = poolOf(s);
  const h = helpings(cells.length);
  const hEcon = economyHelpings(h);
  const fight = Math.max(2, band);
  const spots = hamlets(s, cells, 1 + Math.trunc(h / 2));
  scatterResources(s, cells, spots, (2 + int(s.r, 2)) * h, fight, 2);
  for (let k = 0; k < h; k++) {
    const defId = pick(s.r, cat.chests);
    if (defId !== null) place(s, near(s, cells, spots), defId, nothing, 12);
  }
  if (hEcon > 0) scatterMines(s, zone, cells, 1, fight);
  scatterDwellings(s, zone, cells, pick(s.r, spots), 1);
  for (let k = 0; k < 1 + int(s.r, 2); k++) {
    const defId = pick(s.r, cat.prizeBonuses);
    if (defId !== null) place(s, cells, defId, nothing, 20);
  }
  const shrine = pick(s.r, cat.shrines);
  if (shrine !== null) place(s, cells, shrine, () => shrinePayload(s, fight), 20);
  for (let k = 0; k < 1 + int(s.r, 2); k++) {
    const bank = themed(s, cat.banks, zone);
    if (bank !== null) guardAt(s, place(s, cells, bank, nothing, 25), fight + 1);
  }
  for (let k = 0; k < 1 + int(s.r, 2); k++) {
    const at = place(s, cells, "pu_artifact", () => artifactPayload(s, Math.max(4, fight)), 15);
    if (at >= 0) guardAt(s, at, fight + 1);
  }
  placeCaskets(s, cells, fight);
  place(s, cells, "bld_obelisk", nothing, 15);
  place(s, cells, "tp_garrison", () => garrisonPayload(s, fight), 20);
  scatterGuards(s, cells, (1 + int(s.r, 3)) * h, fight);
}

function populateWaterZone(
  s: GenState, zone: Zone, cells: readonly number[], band: number,
): void {
  const cat = poolOf(s);
  const hw = helpings(cells.length);
  for (let k = 0; k < (2 + int(s.r, 3)) * hw; k++) {
    const defId = pick(s.r, cat.seaPickups);
    if (defId !== null) place(s, cells, defId, nothing, 12);
  }
  for (let k = 0; k < economyHelpings(hw); k++) {
    const boat = pick(s.r, cat.boats);
    if (boat !== null) place(s, cells, boat, nothing, 20);
  }
  for (let k = 0; k < economyHelpings(hw); k++) {
    if (cat.seaBonuses.includes("bank_shipwreck")) {
      place(s, cells, "bank_shipwreck", nothing, 20);
    }
  }
  for (let k = 0; k < (1 + int(s.r, 2)) * economyHelpings(hw); k++) {
    const defId = pick(s.r, cat.seaBonuses);
    if (defId !== null) place(s, cells, defId, nothing, 15);
  }
  // Sea-going stacks, so Saltreach and Blackwater actually appear on the water.
  scatterGuards(s, cells, (1 + int(s.r, 3)) * hw, Math.max(1, band));
}

/** A monster standing in every gap a ridge left open. That is what a gate is. */
export function guardTheGates(s: GenState, gates: readonly number[]): void {
  for (const gate of gates) {
    if (!walkable(s.map, gate) || s.map.occupant[gate] !== 0) continue;
    if (s.map.road[gate] !== 0) continue;
    if (lastDoorstep(s, gate)) continue;
    placeGuard(s, gate, Math.max(1, dangerBand(s, gate)));
  }
}

/**
 * Is `cell` the only free cell beside some object's entrance? A guard there
 * is a guard standing in the doorway: a subterranean gate in a ridge gap on
 * seed 8717 had its one doorstep taken by the gap's own guard, and no hero
 * could stand beside the mouth.
 */
function lastDoorstep(s: GenState, cell: number): boolean {
  const { map: m, g } = s;
  const buf: number[] = [];
  const x = xOf(g, cell), y = yOf(g, cell);
  for (const p of s.placements) {
    for (const e of p.entrances) {
      if (chebyshev(g, e, cell) !== 1 && !(xOf(g, e) === x && yOf(g, e) === y)) continue;
      if (e === cell) continue;
      const free = allNeighbours(g, e, buf)
        .filter((c) => walkable(m, c) && m.occupant[c] === 0).length;
      if (free <= 1) return true;
    }
  }
  return false;
}

/** Monolith pairs, one channel each, mouths in different zones. */
export function linkPortals(s: GenState): void {
  const landZones = s.zones.filter((z) => z.terrain !== "water" && z.cells.length > 40);
  if (landZones.length < 2) return;
  const pairs = clamp(Math.trunc(landZones.length / 2), 1, 4);
  for (let k = 0; k < pairs; k++) {
    const channel = `monolith.${k}`;
    const a = landZones[int(s.r, landZones.length)]!;
    const others = landZones.filter((z) => z.id !== a.id);
    const b = others[int(s.r, others.length)];
    if (b === undefined) continue;
    const defId = k % 2 === 0 ? "tp_two_way_portal" : "tp_monolith";
    const first = place(s, a.cells.filter((i) => walkable(s.map, i)), defId,
      () => ({ channel }), 20);
    if (first < 0) continue;
    const second = place(s, b.cells.filter((i) => walkable(s.map, i)), defId,
      () => ({ channel }), 20);
    // A mouth with nowhere to go is worse than no monolith at all.
    if (second < 0) dropObject(s, first);
  }
}

/**
 * Whirlpool pairs, one channel each, mouths in different bodies of water.
 *
 * One channel per PAIR and not one channel for every whirlpool on the map,
 * which is how the classic wires them and is also the only wiring the route planner
 * can use: `canonicalLinks` drops a mouth that answers with more than one
 * destination, because picking between them is the player's `CHOOSE` and not
 * the planner's guess. Paired, a whirlpool is a real edge in the graph.
 *
 * Both mouths carry `lossBp`, because the toll is charged on arrival - see
 * `teleportTo` in the kernel for why that end is the one that is read.
 */
export function linkWhirlpools(s: GenState): void {
  const sea = s.zones.filter((z) => z.terrain === "water" && z.cells.length > 30);
  if (sea.length < 1) return;
  const pairs = clamp(Math.trunc(sea.length / 2) + 1, 1, 3);
  for (let k = 0; k < pairs; k++) {
    const channel = `whirl.${String(k)}`;
    const a = sea[int(s.r, sea.length)]!;
    // A second mouth in the same body of water is still a legal whirlpool -
    // one sea is the common case on a small map - so a failure to find a
    // different zone is not a reason to skip the pair.
    const others = sea.filter((z) => z.id !== a.id);
    const b = (others.length > 0 ? others[int(s.r, others.length)] : a) ?? a;
    const water = (z: Zone): number[] =>
      z.cells.filter((i) => terrainOf(s.map, i) === "water");
    const payload = (): Payload => ({ channel, lossBp: WHIRLPOOL_LOSS_BP });
    const first = place(s, water(a), "tp_whirlpool", payload, 20);
    if (first < 0) continue;
    const second = place(s, water(b), "tp_whirlpool", payload, 20);
    if (second < 0) dropObject(s, first);
  }
}

/** Half the weakest stack, as the classic charges it. */
const WHIRLPOOL_LOSS_BP = 5000;

/**
 * One-way portal pairs: an entrance somewhere expensive, an exit somewhere
 * else, and no way back.
 *
 * Laid entrance-first and dropped whole if the exit will not site, for the same
 * reason a monolith is: a mouth with nowhere to go strands whoever walks into
 * it. The direction is what makes these worth having at all - a one-way portal
 * out of a guarded pocket is a shortcut you can only spend once per approach.
 *
 * **Neither mouth may sit in anybody's home zone**, and that is the whole
 * difference between a shortcut and a decided game. The first cut of this pass
 * drew from every land zone, exactly as `linkPortals` does for monoliths, and
 * on seed 20260824 it seated an entrance in one capital's zone and the exit in
 * the other's: the opening hero walked into the arch on day 1 and came out on
 * the enemy's doorstep, and the game was over on **day 5**. Measured, on that
 * seed: day 5 with this pass as it was, day 27 with it switched off, day 11
 * with the whole map/object tranche switched off. A monolith is at least
 * two-way and can be walked back through; a one-way express to a capital
 * cannot, which is why the rule is stricter here than there.
 */
export function linkOneWayPortals(s: GenState): void {
  const land = s.zones.filter((z) =>
    z.terrain !== "water" && z.cells.length > 40 && z.role !== "player");
  if (land.length < 2) return;
  const pairs = clamp(Math.trunc(land.length / 3), 1, 2);
  for (let k = 0; k < pairs; k++) {
    const channel = `oneway.${String(k)}`;
    const a = land[int(s.r, land.length)]!;
    const others = land.filter((z) => z.id !== a.id);
    const b = others[int(s.r, others.length)];
    if (b === undefined) continue;
    const cells = (z: Zone): number[] => z.cells.filter((i) => walkable(s.map, i));
    const inAt = place(s, cells(a), "tp_portal_entrance",
      () => ({ channel, entryOnly: true }), 20);
    if (inAt < 0) continue;
    const outAt = place(s, cells(b), "tp_portal_exit",
      () => ({ channel, exitOnly: true }), 20);
    if (outAt < 0) dropObject(s, inAt);
  }
}

/**
 * Mystery Caskets: a one-cell casket, a guard worth opening it for, and a
 * reward written at generation time rather than authored into the def.
 *
 * **A box that could not be guarded is deleted, not shipped.** That is not
 * tidiness, it is the whole rule. The first cut of this pass called `guardAt`
 * and ignored the answer, and `guardAt` fails whenever the doorstep has no free
 * orthogonal neighbour - which on a populated zone is most of the time. Six of
 * nine boxes on a 48x48 map came out unguarded, and an unguarded box is a
 * walk-on prize: seed 20260824 laid one holding nineteen red dragons and one
 * holding 15500 experience, and three of four two-player games were over inside
 * a fortnight because whoever found one first had an army nothing on the map
 * could answer. A box is a fight with a prize behind it or it is not a box.
 *
 * The reward scales with the danger band the box was sited in, which is the
 * only thing that makes a box on the doorstep and a box behind the ridge
 * different objects - they share one def and one sprite.
 */
export function placeCaskets(s: GenState, cells: readonly number[], band: number): void {
  // Three sitings, not one. Dropping an unguardable box costs the zone its box
  // entirely if the first cell happened to be a corner, and a zone with no box
  // is the other way to get this wrong.
  for (let attempt = 0; attempt < 3; attempt++) {
    const at = place(s, cells, "bld_mystery_casket", () => casketPayload(s, band), 20);
    if (at < 0) return;
    if (guardAt(s, at, band + 1)) return;
    dropObject(s, at);
  }
}

/**
 * What is in the box, sized to the fight in front of it.
 *
 * Every arm is deliberately below what the same band's creature bank pays: a
 * bank is a set-piece with a named guard and a box is a scatter object, so a
 * box that out-paid a bank would make the banks pointless and the opening
 * volatile. The creature arm draws from the band's own wanderer pool rather
 * than the band above it, for the same reason.
 */
function casketPayload(s: GenState, band: number): Payload {
  const roll = int(s.r, 4);
  const base: Payload = { consumed: true };
  if (roll === 0) return { ...base, gold: 1000 + band * 1000 };
  if (roll === 1) return { ...base, experience: 1000 + band * 1000 };
  if (roll === 2) return { ...base, ...artifactPayload(s, band) };
  const creature = pickWanderer(s, band, null, s.r);
  if (creature === null) return { ...base, gold: 1000 + band * 1000 };
  return { ...base, rewardCreature: creature.id as string, rewardCount: 2 + band * 2 };
}

/**
 * Oracle's Huts, each with a quest it can actually be paid, and - for one of them
 * - a Quest Guard standing on the far side of a prize.
 *
 * The quest is written here rather than in the def because a hut with no quest
 * hands its reward to the first hero through the door: an unargued Oracle's Hut
 * is a chest with a thatched roof. Which quest a hut asks depends on what the
 * map has: a `resources` quest can always be paid, a `level` quest can always
 * be reached, and both are used in preference to an artifact hunt, which needs
 * an artifact that is actually lying somewhere findable.
 */
export function placeQuests(s: GenState): void {
  const land = s.zones.filter((z) => z.terrain !== "water" && z.cells.length > 40);
  if (land.length === 0) return;
  const huts = clamp(Math.trunc(land.length / 2), 1, 4);
  for (let k = 0; k < huts; k++) {
    const zone = land[int(s.r, land.length)]!;
    const cells = zone.cells.filter((i) => walkable(s.map, i));
    if (cells.length === 0) continue;
    const band = dangerBand(s, zone.seed);
    const channel = `quest.${String(k)}`;
    place(s, cells, "bld_oracle_hut", () => questPayload(s, band, channel), 20);
  }
}

function questPayload(s: GenState, band: number, channel: string): Payload {
  const ask = int(s.r, 3);
  const reward: Payload = int(s.r, 2) === 0
    ? { gold: 2000 + band * 1500, experience: 1000 + band * 750 }
    : { ...artifactPayload(s, band), experience: 1000 + band * 750 };
  const common: Payload = { service: "seer", questChannel: channel, ...reward };

  if (ask === 0) {
    const r = pick(s.r, RESOURCE_TRIBUTES) ?? "wood";
    const amount = r === "gold" ? 5000 + band * 2500 : 8 + band * 4;
    return { ...common, quest: "resources", questResource: r, questAmount: amount };
  }
  if (ask === 1) {
    return { ...common, quest: "level", questLevel: clamp(4 + band * 2, 4, 15) };
  }
  // Comfortably above what a hero walks out of his own capital with, or the
  // hut pays on day one for standing still: `startingGarrison` is sized to
  // `START_GARRISON_VALUE` and an opening escort is a few hundred more.
  return {
    ...common, quest: "army",
    questArmy: START_GARRISON_VALUE * 2 + band * 3000,
  };
}

const RESOURCE_TRIBUTES: readonly string[] =
  Object.freeze(["gold", "wood", "ore", "mercury", "sulfur", "crystal", "gems"]);

/**
 * A walled treasure pocket with one gated way in, and a Keymaster's Tent
 * somewhere else holding the key. This is the one place the generator builds a
 * *shape* instead of scattering, and it is what makes a border gate mean
 * something.
 */
export function buildVault(s: GenState): void {
  let neutral = shuffle(s.r,
    s.zones.filter((z) => z.job === "dead-end" || z.job === "treasure").slice());
  if (neutral.length === 0) {
    neutral = shuffle(s.r,
      s.zones.filter((z) => z.role === "neutral" && z.terrain !== "water").slice());
  }
  const g = s.g;
  for (const zone of neutral) {
    if (tryVault(s, zone, g)) return;
  }
}

function tryVault(s: GenState, zone: Zone, g: Grid): boolean {
  const candidates = shuffle(s.r, zone.cells.filter((i) => walkable(s.map, i)).slice())
    .slice(0, 300);

  for (const centre of candidates) {
    const cx = xOf(g, centre), cy = yOf(g, centre);
    let clear = true;
    for (let dy = -2; dy <= 2 && clear; dy++) {
      for (let dx = -2; dx <= 2 && clear; dx++) {
        if (!inside(g, cx + dx, cy + dy)) { clear = false; break; }
        const i = idx(g, cx + dx, cy + dy);
        if (!walkable(s.map, i) || s.map.occupant[i] !== 0 || s.map.road[i] !== 0) clear = false;
        else if (s.ctx.reserved[i] === 1) clear = false;
      }
    }
    // ...and one ring further out for ROADS only. The vault's wall of blocking
    // scenery IS the ring at +/-2, so a road at +/-3 is a road with a thicket on
    // its verge - the one thing `decorate` takes care never to lay, and the
    // thing `mapgen-landform.spec.ts` asserts. The box above cannot see it: it
    // stops at the ring itself. Roads only, because widening the whole test
    // would ask for a seven-by-seven clearing and there are not many of those.
    for (let dy = -3; dy <= 3 && clear; dy++) {
      for (let dx = -3; dx <= 3 && clear; dx++) {
        if (Math.abs(dx) !== 3 && Math.abs(dy) !== 3) continue;
        if (!inside(g, cx + dx, cy + dy)) continue;
        if (s.map.road[idx(g, cx + dx, cy + dy)] !== 0) clear = false;
      }
    }
    if (!clear) continue;

    const band = Math.max(3, dangerBand(s, centre));
    const blockers = (SCENERY[terrainOf(s.map, centre)]?.blocking ?? [])
      .filter((b) => (s.content.mapObjects.get(b)?.footprint.length ?? 0) === 1);
    if (blockers.length === 0) continue;

    const gate = idx(g, cx, cy + 2);
    for (let dy = -2; dy <= 2; dy++) {
      for (let dx = -2; dx <= 2; dx++) {
        if (Math.abs(dx) !== 2 && Math.abs(dy) !== 2) continue;
        const i = idx(g, cx + dx, cy + dy);
        if (i === gate) continue;
        const b = pick(s.r, blockers);
        if (b !== null) place(s, [i], b, nothing, 1);
      }
    }
    place(s, [gate], "tp_border_guard_post", () => ({ colour: "vault" }), 1);
    place(s, [centre], "pu_treasure_hoard", nothing, 1);
    for (const [dx, dy] of [[-1, 0], [1, 0], [0, -1]] as const) {
      place(s, [idx(g, cx + dx, cy + dy)], "pu_artifact", () => artifactPayload(s, band), 1);
    }

    const elsewhere = s.zones
      .filter((z) => z.id !== zone.id && z.terrain !== "water")
      .flatMap((z) => z.cells)
      .filter((i) => walkable(s.map, i));
    place(s, elsewhere, "tp_keymaster_tent", () => ({ colour: "vault" }), 40);
    return true;
  }
  return false;
}
